import { ConnectorGroup, Connector } from '@/types/rms_v2.type'
import { UserGroup } from '@/types/user.type'
import { STORAGE_CONNECTOR_ROLES } from '@/constants/roles_and_permissions'

/**
 * Connector별 동작 모드 설정
 * Role 기반으로 connector의 동작 방식과 UI 표시를 결정
 */
interface ConnectorModeConfig {
  /** 이 모드를 활성화하는 Role 이름 */
  role: string
  /** Connected 상태에서 이메일 대신 표시할 metadata 필드 (없으면 email 표시) */
  displayField?: string
  /** 표시 prefix (예: "Site:" → "Site: 개발팀") */
  displayPrefix?: string
  /** Sync 시 workspace 선택 모달 스킵 여부 */
  skipSyncModal?: boolean
  /** 기본 sync workspace (skipSyncModal=true일 때 사용) */
  defaultSyncWorkspace?: 'organization' | 'personal'
  /** Connect 시 OAuth 대신 커스텀 모달 사용 여부 */
  useCustomConnectModal?: boolean
  /** FileTable에서 Remove/Resync 차단 여부 */
  blockItemActions?: boolean
}

interface ConnectorConfig {
  id: Connector
  name: string
  description: string
  isAvailable: boolean
  possibleGroups?: UserGroup[]
  /**
   * Role 기반 접근 제어
   * NOTE: 현재는 Role을 Permission처럼 직접 사용
   * TODO: 향후 Permission 기반 전환 시 requiredPermissions로 변경
   * 참고: .claude/plan/RBAC_MIGRATION_PLAN.md
   */
  requiredRoles?: string[]
  /** 특수 동작 모드 설정 (Role 기반 분기) */
  modes?: ConnectorModeConfig[]
  icon: string
  oauthScope: string
  pickerScope?: string
  fileBaseUrl?: string
  folderBaseUrl?: string
  downloadBaseUrl?: string
}

interface ConnectorGroupConfig {
  id: ConnectorGroup
  title: string
  description: string
  connectors: ConnectorConfig[]
}

const defaultGroups = [UserGroup.Internal, UserGroup.StorageUse]
// advancedGroups: Outlook/Gmail 커넥터 노출 대상. sub_org_storage_use 제거 후 값은
// InternalGroups와 같아졌지만, 커넥터별 의미(advanced vs internal-only)가 달라 의도적으로
// 분리 유지한다.
const advancedGroups = [UserGroup.Internal]

// InternalGroups: OneNote 등 internal 전용 커넥터 노출 대상.
const InternalGroups = [UserGroup.Internal]

const VaultGroups = [UserGroup.Internal, UserGroup.StorageUse]

// Main grouped connectors configuration
export const connectorGroups: ConnectorGroupConfig[] = [
  {
    id: ConnectorGroup.Drive,
    title: 'Drive Connectors',
    description:
      'Connect your cloud drives to integrate relevant files into LinqAlpha search results.',
    connectors: [
      {
        id: Connector.GoogleDrive,
        name: 'Google Drive',
        description: 'Your search results include files from Google Drive.',
        isAvailable: true,
        possibleGroups: defaultGroups,
        // Role: storage_google_drive_connectable_user
        requiredRoles: [STORAGE_CONNECTOR_ROLES.GoogleDrive],
        icon: '/images/connectors/googleDrive.png',
        oauthScope:
          'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/userinfo.email',
        pickerScope: 'https://www.googleapis.com/auth/drive.file',
        fileBaseUrl: 'https://drive.google.com/file/d/',
        folderBaseUrl: 'https://drive.google.com/drive/folders/',
        downloadBaseUrl: 'https://drive.google.com/uc?export=download&id=',
      },
      {
        id: Connector.OneDrive,
        name: 'OneDrive',
        description: 'Your search results include files from OneDrive.',
        isAvailable: true,
        possibleGroups: defaultGroups,
        // Role: storage_onedrive_connectable_user
        requiredRoles: [STORAGE_CONNECTOR_ROLES.OneDrive],
        icon: '/images/connectors/oneDrive.png',
        oauthScope:
          'https://graph.microsoft.com/Files.Read.All https://graph.microsoft.com/User.Read offline_access',
        pickerScope: 'https://graph.microsoft.com/Files.Read.All',
        fileBaseUrl: 'https://1drv.ms/f/',
        folderBaseUrl: 'https://1drv.ms/f/',
        downloadBaseUrl: 'https://graph.microsoft.com/v1.0/me/drive/items/',
      },
      {
        id: Connector.SharePoint,
        name: 'SharePoint',
        description: 'Your search results include files from SharePoint.',
        isAvailable: true,
        // SharePoint는 role 기반으로만 노출 (group으로 노출하지 않음)
        // 기존 storage_use/advanced_storage_use 유저에게는 role 일괄 부여 완료
        requiredRoles: [STORAGE_CONNECTOR_ROLES.SharePoint, STORAGE_CONNECTOR_ROLES.SharePointApp],
        modes: [
          {
            role: STORAGE_CONNECTOR_ROLES.SharePointApp,
            displayField: 'site_name',
            displayPrefix: 'Site',
            skipSyncModal: true,
            defaultSyncWorkspace: 'organization',
            useCustomConnectModal: true,
            blockItemActions: true,
          },
        ],
        icon: '/images/connectors/sharePoint.png',
        oauthScope:
          'https://graph.microsoft.com/Sites.Read.All https://graph.microsoft.com/User.Read offline_access',
        pickerScope: 'https://graph.microsoft.com/Sites.Read.All',
        fileBaseUrl: 'https://sharepoint.com/sites/',
        folderBaseUrl: 'https://sharepoint.com/sites/',
        downloadBaseUrl: 'https://graph.microsoft.com/v1.0/sites/',
      },
    ],
  },
  {
    id: ConnectorGroup.Email,
    title: 'Email Connectors',
    description:
      'Connect your email accounts to include relevant documents and context in LinqAlpha.',
    connectors: [
      {
        id: Connector.Outlook,
        name: 'Outlook',
        description: 'Your search results include email from Outlook.',
        isAvailable: true,
        possibleGroups: advancedGroups,
        // Role: storage_outlook_connectable_user
        requiredRoles: [STORAGE_CONNECTOR_ROLES.Outlook],
        icon: '/images/connectors/outlook.png',
        oauthScope:
          'https://graph.microsoft.com/Mail.Read https://graph.microsoft.com/User.Read offline_access',
        pickerScope: 'https://graph.microsoft.com/Mail.Read',
      },
      {
        id: Connector.Gmail,
        name: 'Gmail',
        description: 'Your search results include email from Gmail.',
        isAvailable: false,
        possibleGroups: advancedGroups,
        // NOTE: Gmail은 현재 미오픈으로 Role 기반 접근 제어 미적용
        icon: '/images/connectors/gmail.png',
        oauthScope:
          'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email',
        pickerScope: 'https://www.googleapis.com/auth/gmail.readonly',
      },
    ],
  },
  {
    id: ConnectorGroup.S3,
    title: 'S3 Connectors',
    description: 'Connect to S3 services',
    connectors: [
      {
        id: Connector.eds,
        name: 'eds',
        description: 'Your search results include EDS files',
        isAvailable: false,
        possibleGroups: [UserGroup.Internal],
        // NOTE: EDS는 커넥터 공개 계획 없음으로 Role 기반 접근 제어 미적용
        icon: '/images/connectors/eds.png',
        oauthScope:
          'https://graph.microsoft.com/S3.Read.All https://graph.microsoft.com/User.Read offline_access',
        pickerScope: 'https://graph.microsoft.com/S3.Read.All',
      },
      {
        id: Connector.s3,
        name: 'S3',
        description: 'Your search results include S3 files',
        isAvailable: false,
        possibleGroups: [UserGroup.Internal],
        // NOTE: S3는 커넥터 공개 계획 없음으로 Role 기반 접근 제어 미적용
        icon: '/images/connectors/s3.svg',
        oauthScope: '',
        pickerScope: '',
      },
      {
        id: Connector.CLSA,
        name: 'CLSA',
        description: 'Your search results include CLSA files',
        isAvailable: false,
        possibleGroups: [UserGroup.Internal],
        // NOTE: CLSA는 backend-controlled (sync_user@clsa.com), 사용자 OAuth 없음. s3와 동일 패턴
        icon: '/images/connectors/s3.svg',
        oauthScope: '',
        pickerScope: '',
      },
      {
        id: Connector.ThirdBridge,
        name: 'Third Bridge',
        description: 'Your search results include Third Bridge files',
        isAvailable: false,
        possibleGroups: [UserGroup.Internal],
        // NOTE: Third Bridge는 backend-controlled, 사용자 OAuth 없음. CLSA/s3와 동일 패턴
        icon: '/images/connectors/s3.svg',
        oauthScope: '',
        pickerScope: '',
      },
      {
        id: Connector.MeetingAgent,
        name: 'Meeting Agent',
        description: 'Your search results include meeting transcripts.',
        // Read-only, backend-managed vault-family source. Always-connected via the
        // searchSourceStore (alwaysConnectedConnectors), like Vault — not surfaced in
        // the "Add Connectors" gallery.
        isAvailable: false,
        icon: '/images/connectors/meetingAgent.svg',
        oauthScope: '',
        pickerScope: '',
      },
      {
        id: Connector.LinqDrive,
        name: 'Linq Drive',
        description: 'Your search results include files synced from Linq Drive.',
        // Backend-managed desktop sync (rms-server S3-family source). No user OAuth,
        // not surfaced in the "Add Connectors" gallery.
        isAvailable: false,
        icon: '/images/connectors/linqDrive.svg',
        oauthScope: '',
        pickerScope: '',
      },
      {
        id: Connector.EchoAgent,
        name: 'Echo Agent',
        description:
          'Your search results include meeting notes and transcripts synced from Echo Agent.',
        // Same backend-managed desktop sync as Linq Drive, and shares its icon.
        isAvailable: false,
        icon: '/images/connectors/linqDrive.svg',
        oauthScope: '',
        pickerScope: '',
      },
    ],
  },
  {
    id: ConnectorGroup.Note,
    title: 'Note Connectors',
    description: 'Connect your note-taking apps to include notebooks in LinqAlpha.',
    connectors: [
      {
        id: Connector.OneNote,
        name: 'OneNote',
        description: 'Your search results include notebooks from OneNote.',
        isAvailable: true,
        possibleGroups: InternalGroups,
        // Role: storage_onenote_connectable_user
        requiredRoles: [STORAGE_CONNECTOR_ROLES.OneNote],
        icon: '/images/connectors/oneNote.png',
        oauthScope:
          'https://graph.microsoft.com/Notes.Read https://graph.microsoft.com/Notes.Read.All https://graph.microsoft.com/User.Read offline_access',
        pickerScope: 'https://graph.microsoft.com/Notes.Read.All',
      },
    ],
  },
  {
    id: ConnectorGroup.Vault,
    title: 'Vault',
    description: 'Upload your own files directly to LinqAlpha for search and analysis.',
    connectors: [
      {
        id: Connector.Vault,
        name: 'Local Files',
        description: 'Your search results include uploaded files.',
        isAvailable: false,
        possibleGroups: VaultGroups,
        icon: '/images/connectors/vault.svg',
        oauthScope: '',
        pickerScope: '',
      },
    ],
  },
]

// Create a flat map for backward compatibility
export const connectors = connectorGroups.reduce((acc, group) => {
  group.connectors.forEach((connector) => {
    acc[connector.id] = connector
  })
  return acc
}, {} as Record<string, ConnectorConfig>)

// Helper function to get connector by ID
export const getConnectorById = (id: Connector): ConnectorConfig | undefined => {
  return connectors[id]
}

// Helper function to get group by connector ID
export const getGroupByConnectorId = (connectorId: Connector): ConnectorGroupConfig | undefined => {
  return connectorGroups.find((group) =>
    group.connectors.some((connector) => connector.id === connectorId)
  )
}

// Helper to get Name by Connector Type
export const getConnectorNameByType = (id: Connector): string | undefined => {
  return Object.values(connectors).find((connector) => connector.id === id)?.name
}

// Helper function to get all available connectors
export const getAvailableConnectors = (): ConnectorConfig[] => {
  return Object.values(connectors).filter((connector) => connector.isAvailable)
}

// Helper function to get connectors by group
export const getConnectorsByGroup = (groupId: ConnectorGroup): ConnectorConfig[] => {
  const group = connectorGroups.find((g) => g.id === groupId)
  return group?.connectors || []
}

// Additional utility functions
export const getAvailableConnectorsByGroup = (groupId: ConnectorGroup): ConnectorConfig[] => {
  return getConnectorsByGroup(groupId).filter((connector) => connector.isAvailable)
}

/**
 * 유저의 Role 목록을 기반으로 connector의 active mode를 반환
 * modes 배열에서 유저가 가진 role과 매칭되는 첫 번째 mode를 반환
 * 매칭되는 mode가 없으면 null (기본 동작)
 */
export const getActiveConnectorMode = (
  connectorId: Connector,
  userRoles: { name: string }[]
): ConnectorModeConfig | null => {
  const config = getConnectorById(connectorId)
  if (!config?.modes) return null

  const roleNames = new Set(userRoles.map((r) => r.name))
  return config.modes.find((mode) => roleNames.has(mode.role)) ?? null
}

// Type exports for backward compatibility
export type ConnectorsMap = typeof connectors
export type ConnectorId = Connector // Use your existing enum

// Re-export the enums and types
export {
  ConnectorGroup,
  Connector,
  type ConnectorConfig,
  type ConnectorGroupConfig,
  type ConnectorModeConfig,
}
